import { useCallback, useEffect, useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { listUsers } from "./users.gateway";
import { DEFAULT_USERS_SORT } from "./users.types";
import type {
  UserFilterOption,
  UserRole,
  UsersQueryState,
  UsersSort,
  UserStatus,
} from "./users.types";

export const SEARCH_DEBOUNCE_MS = 300;

export const USERS_FORBIDDEN_MESSAGE = "Você não tem permissão para consultar os usuários do Hub.";
export const USERS_ERROR_MESSAGE = "Não foi possível carregar os usuários.";

const INITIAL_STATE: UsersQueryState = {
  status: "loading",
  users: [],
  total: 0,
  semPapel: 0,
  errorMessage: null,
};

interface FilterOptions {
  times: UserFilterOption[];
  papeis: UserFilterOption[];
}

const NO_OPTIONS: FilterOptions = { times: [], papeis: [] };

/**
 * As opções vistas são acumuladas para que aplicar um filtro — que reduz a
 * listagem — não esvazie os seletores de Time e Perfil.
 */
function mergeOptions(
  current: UserFilterOption[],
  incoming: UserFilterOption[],
): UserFilterOption[] {
  const known = new Set(current.map((option) => option.value));
  const added = incoming.filter((option) => !known.has(option.value));
  if (added.length === 0) return current;

  return [...current, ...added].sort((a, b) =>
    a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }),
  );
}

export interface UseUsersParams {
  search: string;
  time: string;
  papelId: string;
  page: number;
  pageSize: number;
  sort?: UsersSort;
}

interface UseUsersResult extends UsersQueryState, FilterOptions {
  reload: () => void;
  /**
   * Reflete na listagem em memória um status já alterado, sem nova consulta.
   * Só o status é tocado: o perfil e os demais dados da linha seguem como estão.
   */
  applyUserStatus: (id: string, status: UserStatus) => void;
  /** Reflete na listagem o perfil recém-gravado, sem nova consulta ao serviço. */
  applyUserRole: (id: string, role: UserRole) => void;
}

export function useUsers({
  search,
  time,
  papelId,
  page,
  pageSize,
  sort = DEFAULT_USERS_SORT,
}: UseUsersParams): UseUsersResult {
  const [state, setState] = useState<UsersQueryState>(INITIAL_STATE);
  const [filters, setFilters] = useState<FilterOptions>(NO_OPTIONS);
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();

  useEffect(() => {
    // Debounce só na digitação; carga inicial, filtros, paginação e retry são imediatos.
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        listUsers({
          pesquisa: term,
          time,
          papelId,
          page,
          pageSize,
          ordenacao: sort,
          signal: controller.signal,
        })
          .then(({ users, total, semPapel, times, papeis }) => {
            if (cancelled) return;

            setState({ status: "success", users, total, semPapel, errorMessage: null });
            setFilters((current) => {
              const merged = {
                times: mergeOptions(current.times, times),
                papeis: mergeOptions(current.papeis, papeis),
              };

              return merged.times === current.times && merged.papeis === current.papeis
                ? current
                : merged;
            });
          })
          .catch((error: unknown) => {
            if (cancelled) return;

            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              users: [],
              total: 0,
              semPapel: 0,
              errorMessage: isForbidden
                ? USERS_FORBIDDEN_MESSAGE
                : getErrorMessage(error) || USERS_ERROR_MESSAGE,
            });
          });
      },
      shouldDebounce ? SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, time, papelId, page, pageSize, sort, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const applyUserStatus = useCallback((id: string, status: UserStatus) => {
    setState((previous) => ({
      ...previous,
      users: previous.users.map((item) => (item.id === id ? { ...item, status } : item)),
    }));
  }, []);

  const applyUserRole = useCallback((id: string, role: UserRole) => {
    setState((previous) => {
      const current = previous.users.find((user) => user.id === id);
      if (!current) return previous;

      // O alerta do cabeçalho conta os pendentes: só muda quando a pendência muda.
      const delta = (current.papel ? 0 : 1) - (role.papel ? 0 : 1);

      return {
        ...previous,
        users: previous.users.map((user) => (user.id === id ? { ...user, ...role } : user)),
        semPapel: Math.max(0, previous.semPapel - delta),
      };
    });
  }, []);

  return { ...state, ...filters, reload, applyUserStatus, applyUserRole };
}
